import React from 'react';
import {
  Volume2,
  Sliders,
  Palette,
  Vibrate,
  RotateCw,
  Trash2,
  RotateCcw,
  Check,
} from 'lucide-react';
import { UserSettings, DspState } from '../types';
import { tg } from '../services/telegram';
import {
  userStore,
  STREAM_QUALITY_OPTIONS,
  THEME_OPTIONS,
  EQ_PRESETS,
} from '../services/userStore';
import { audioEngine } from '../services/audioEngine';

interface SettingsViewProps {
  settings: UserSettings;
  dsp: DspState;
}

export const SettingsView: React.FC<SettingsViewProps> = ({
  settings,
  dsp,
}) => {
  const formatDb = (db: number) => {
    return `${db > 0 ? '+' : ''}${db.toFixed(1)} дБ`;
  };

  const formatBalance = (bal: number) => {
    if (Math.abs(bal) < 0.05) return 'Центр (L 50% / R 50%)';
    if (bal < 0) return `Левый канал (L ${Math.round((1 - bal) * 50)}%)`;
    return `Правый канал (R ${Math.round((1 + bal) * 50)}%)`;
  };

  return (
    <div className="settings-view-container stagger-item stagger-2">
      {/* 1. Quality Preference Section */}
      <section className="settings-card">
        <div className="settings-card-header">
          <Volume2 size={15} color="var(--amber)" />
          <h3 className="settings-card-title">КАЧЕСТВО АУДИОПОТОКА</h3>
        </div>
        <p className="settings-card-caption">
          Выбор целевого профиля декодирования и максимального разрешения источника:
        </p>

        <div className="settings-options-list">
          {STREAM_QUALITY_OPTIONS.map((opt) => {
            const isSelected = settings.streamQuality === opt.id;
            return (
              <div
                key={opt.id}
                className={`settings-option-item ${isSelected ? 'selected' : ''}`}
                onClick={() => {
                  tg.haptic('medium');
                  userStore.updateSettings({ streamQuality: opt.id });
                }}
              >
                <div className="option-radio-dot">
                  {isSelected && <div className="option-radio-inner" />}
                </div>
                <div className="option-body">
                  <div className="option-title-row">
                    <span className="option-title">{opt.label}</span>
                    <span className="option-badge">{opt.badge}</span>
                  </div>
                  <div className="option-desc">{opt.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 2. DSP & Tone Presets Section */}
      <section className="settings-card">
        <div className="settings-card-header">
          <Sliders size={15} color="var(--amber)" />
          <h3 className="settings-card-title">ПРЕСЕТЫ ТЕМБРОБЛОКА (DSP)</h3>
        </div>
        <p className="settings-card-caption">
          Аналоговая коррекция кривой АЧХ. Выбор пресета сразу синхронизирует крутилки деки:
        </p>

        <div className="dsp-presets-grid">
          {(Object.keys(EQ_PRESETS) as Array<keyof typeof EQ_PRESETS>).map((key) => {
            if (key === 'custom') return null;
            const preset = EQ_PRESETS[key];
            const isSelected = settings.eqPreset === key;
            return (
              <button
                key={key}
                type="button"
                className={`dsp-preset-chip ${isSelected ? 'active' : ''}`}
                onClick={() => {
                  tg.haptic('medium');
                  userStore.applyEqPreset(key);
                }}
              >
                <span className="preset-chip-title">{preset.label}</span>
                <span className="preset-chip-spec">
                  НЧ {preset.bass > 0 ? `+${preset.bass}` : preset.bass} / ВЧ{' '}
                  {preset.treble > 0 ? `+${preset.treble}` : preset.treble}
                </span>
                {isSelected && <Check size={12} className="preset-check" />}
              </button>
            );
          })}
        </div>

        {/* Live DSP Telemetry readout */}
        <div className="dsp-readout-panel">
          <div className="readout-row">
            <span className="readout-label">НИЗКИЕ ЧАСТОТЫ (BASS):</span>
            <span className="readout-val">{formatDb(dsp.bassDb)}</span>
          </div>
          <div className="readout-row">
            <span className="readout-label">ВЫСОКИЕ ЧАСТОТЫ (TREBLE):</span>
            <span className="readout-val">{formatDb(dsp.trebleDb)}</span>
          </div>
          <div className="readout-row">
            <span className="readout-label">СТЕРЕОБАЛАНС:</span>
            <span className="readout-val">{formatBalance(dsp.balance)}</span>
          </div>

          <button
            type="button"
            className="btn-reset-knobs"
            onClick={() => {
              tg.haptic('medium');
              userStore.applyEqPreset('flat');
              audioEngine.setBalance(0);
            }}
          >
            <RotateCcw size={12} />
            <span>Сбросить крутилки в 0 (Линейный тракт)</span>
          </button>
        </div>
      </section>

      {/* 3. Theme & Visual Appearance */}
      <section className="settings-card">
        <div className="settings-card-header">
          <Palette size={15} color="var(--amber)" />
          <h3 className="settings-card-title">ОФОРМЛЕНИЕ ИНТЕРФЕЙСА (ТЕМА)</h3>
        </div>
        <p className="settings-card-caption">
          Выберите цветовую гамму кассетного река и Telegram WebApp:
        </p>

        <div className="theme-cards-list">
          {THEME_OPTIONS.map((t) => {
            const isSelected = settings.theme === t.id;
            return (
              <div
                key={t.id}
                className={`theme-card-item ${isSelected ? 'selected' : ''}`}
                onClick={() => {
                  tg.haptic('medium');
                  userStore.updateSettings({ theme: t.id });
                }}
              >
                <div
                  className="theme-card-swatch"
                  style={{ backgroundColor: t.accent }}
                />
                <div className="theme-card-info">
                  <div className="theme-card-title-row">
                    <span className="theme-card-name">{t.label}</span>
                    {isSelected && (
                      <span className="theme-active-tag">АКТИВНА</span>
                    )}
                  </div>
                  <div className="theme-card-desc">{t.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 4. Player & Interface Behavior */}
      <section className="settings-card">
        <div className="settings-card-header">
          <Sliders size={15} color="var(--amber)" />
          <h3 className="settings-card-title">ПОВЕДЕНИЕ ПЛЕЕРА И ИНТЕРФЕЙС</h3>
        </div>

        <div className="toggle-setting-row">
          <div className="toggle-setting-text">
            <div className="toggle-title">
              <Vibrate size={14} color="var(--amber)" />
              <span>Тактильный отклик (вибрация)</span>
            </div>
            <div className="toggle-sub">
              Фирменные механические щелчки кнопок и переключателей в Telegram
            </div>
          </div>
          <button
            type="button"
            className={`mech-toggle-btn ${settings.hapticsEnabled ? 'on' : 'off'}`}
            onClick={() => {
              const next = !settings.hapticsEnabled;
              userStore.updateSettings({ hapticsEnabled: next });
              if (next) tg.haptic('medium');
            }}
            aria-pressed={settings.hapticsEnabled}
          >
            <span className="mech-toggle-thumb" />
          </button>
        </div>

        <div className="toggle-setting-row">
          <div className="toggle-setting-text">
            <div className="toggle-title">
              <RotateCw size={14} color="var(--amber)" />
              <span>Автопереключение на следующий трек</span>
            </div>
            <div className="toggle-sub">
              Непрерывное воспроизведение кассетной очереди и мастер-альбомов
            </div>
          </div>
          <button
            type="button"
            className={`mech-toggle-btn ${settings.autoPlayNext ? 'on' : 'off'}`}
            onClick={() => {
              tg.haptic('light');
              userStore.updateSettings({ autoPlayNext: !settings.autoPlayNext });
            }}
            aria-pressed={settings.autoPlayNext}
          >
            <span className="mech-toggle-thumb" />
          </button>
        </div>
      </section>

      {/* 5. Memory & Data Reset */}
      <section className="settings-card">
        <div className="settings-card-header">
          <Trash2 size={15} color="var(--ruby)" />
          <h3 className="settings-card-title" style={{ color: 'var(--ruby)' }}>
            ПАМЯТЬ И СБРОС
          </h3>
        </div>

        <div className="settings-actions-stack">
          <button
            type="button"
            className="btn-danger-outline"
            onClick={() => {
              tg.haptic('medium');
              userStore.clearHistory();
            }}
          >
            <Trash2 size={13} />
            <span>Очистить историю прослушиваний ({userStore.history.length})</span>
          </button>

          <button
            type="button"
            className="btn-danger-outline"
            onClick={() => {
              tg.haptic('heavy');
              userStore.resetSettings();
            }}
          >
            <RotateCcw size={13} />
            <span>Сбросить все настройки по умолчанию</span>
          </button>
        </div>
      </section>
    </div>
  );
};
